import type { CompressedTexture, LoadingManager, Texture } from 'three'
import { MeshLoadCancelledError, throwIfMeshLoadCancelled } from '@/plugins/mesh/errors'
import { estimateThreeTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { getConcreteGltfTextureMaxSize } from '@/plugins/mesh/loaders/gltfLowMemory/gltfTextureAutoSize'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import type { IMeshKtx2Service } from '@/plugins/mesh/loaders/MeshKtx2Service'
import type { MeshTextureTaskPool } from '@/plugins/mesh/loaders/MeshTextureTaskPool'
import { selectMeshKtx2Mips } from '@/plugins/mesh/loaders/selectMeshKtx2Mips'
import type { MeshLoadRequest } from '@/plugins/mesh/types'

/** 仅接受二维 LDR BasisU；选择 mip 前检查容器头，转码器继续校验完整数据。 */
export function validateMeshKtx2(data: ArrayBuffer, maxSize: number): void {
  const magic = [171, 75, 84, 88, 32, 50, 48, 187, 13, 10, 26, 10]
  if (data.byteLength < 80 || !magic.every((value, index) => new Uint8Array(data)[index] === value))
    throw new Error('KTX2 容器标识或文件头无效')
  const view = new DataView(data)
  const width = view.getUint32(20, true)
  const height = view.getUint32(24, true)
  const dfdOffset = view.getUint32(48, true)
  const dfdLength = view.getUint32(52, true)
  if (dfdLength < 16 || dfdOffset + dfdLength > data.byteLength)
    throw new Error('KTX2 DFD 范围无效')
  const colorModel = view.getUint8(dfdOffset + 12)
  if (
    view.getUint32(12, true) !== 0 ||
    ![163, 166].includes(colorModel) ||
    width === 0 ||
    height === 0 ||
    view.getUint32(28, true) !== 0 ||
    view.getUint32(32, true) !== 0 ||
    view.getUint32(36, true) !== 1 ||
    view.getUint32(40, true) === 0
  )
    throw new Error('KTX2 仅支持二维 LDR ETC1S/UASTC，不支持 HDR、数组或立方体')
  if (width > maxSize || height > maxSize)
    throw new Error(`KTX2 源尺寸 ${width}x${height} 超过 GPU maxTextureSize=${maxSize}`)
}

/** 请求专属读取和任务调度；不改变共享 KTX2Loader 的 manager/header/path。 */
export class Ktx2RequestLoader {
  private readonly _controller = new AbortController()
  private readonly _owned = new Set<Texture>()
  private readonly _disposedTextures = new WeakSet<Texture>()
  private readonly _listeners = new Map<Texture, () => void>()
  private _closed = false
  encodedBytes = 0
  transcodedBytes = 0

  constructor(
    private readonly _service: IMeshKtx2Service,
    private readonly _manager: LoadingManager,
    private readonly _request: MeshLoadRequest,
    private readonly _pool: MeshTextureTaskPool,
    private readonly _onError: (error: unknown) => void
  ) {
    if (_request.signal?.aborted) this._abort()
    else _request.signal?.addEventListener('abort', this._abort, { once: true })
  }

  private readonly _abort = () => this._controller.abort(this._request.signal?.reason)

  /** 内嵌 bufferView 不创建 blob URL；字节在转码服务中复制后交给 Worker。 */
  async load(source: string | Promise<ArrayBuffer>): Promise<CompressedTexture> {
    const url = typeof source === 'string' ? this._manager.resolveURL(source) : '(KTX2 bufferView)'
    this._manager.itemStart(url)
    let release: (() => void) | undefined
    try {
      release = await this._pool.acquire(this._controller.signal)
      throwIfMeshLoadCancelled(this._controller.signal, url)
      const data = typeof source === 'string' ? await this.fetch(url) : await source
      throwIfMeshLoadCancelled(this._controller.signal, url)
      validateMeshKtx2(data, Infinity)
      const maxSize = Math.min(
        getConcreteGltfTextureMaxSize(resolveGltfLoadingOptions(undefined, this._request.gltf)),
        this._service.maxTextureSize
      )
      const selected = selectMeshKtx2Mips(data, maxSize)
      validateMeshKtx2(selected, this._service.maxTextureSize)
      this.encodedBytes += data.byteLength
      // Worker 单任务无法取消：保持额度直到迟到输出清理完成。
      const texture = await this._service.transcode(selected)
      this.transcodedBytes += estimateThreeTextureBytes(texture)
      this.track(texture)
      return texture
    } catch (error) {
      const cancelled = this._controller.signal.aborted || error instanceof MeshLoadCancelledError
      this._onError(error)
      if (!cancelled) this._manager.itemError(url)
      throw error
    } finally {
      release?.()
      this._manager.itemEnd(url)
    }
  }

  private async fetch(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url, {
      headers: this._request.headers,
      credentials: this._request.credentials ?? 'same-origin',
      signal: this._controller.signal,
    })
    if (!response.ok) throw new Error(`KTX2 请求失败: ${response.status} ${url}`)
    return response.arrayBuffer()
  }

  /** 跟踪 Three.js 为 sampler、UV 或颜色空间创建的独立 Texture。 */
  track<T extends Texture>(texture: T): T {
    if (this._closed || this._controller.signal.aborted) {
      texture.dispose()
      this._disposedTextures.add(texture)
      throw new MeshLoadCancelledError('(KTX2 请求已结束)')
    }
    if (!this._owned.has(texture)) {
      this._owned.add(texture)
      const disposed = () => {
        this._disposedTextures.add(texture)
        this.release(texture)
      }
      this._listeners.set(texture, disposed)
      texture.addEventListener('dispose', disposed)
    }
    return texture
  }

  private release(texture: Texture): void {
    const listener = this._listeners.get(texture)
    if (listener) texture.removeEventListener('dispose', listener)
    this._listeners.delete(texture)
    this._owned.delete(texture)
  }

  wasDisposed(texture: Texture): boolean {
    return this._disposedTextures.has(texture)
  }

  /** 成功后仅将场景实际使用的纹理交给结果，清理解析缓存中的未使用克隆。 */
  finish(used: ReadonlySet<Texture>): void {
    for (const texture of used) this.release(texture)
    this.dispose()
  }

  dispose(): void {
    if (this._closed) return
    this._closed = true
    this._request.signal?.removeEventListener('abort', this._abort)
    this._controller.abort('KTX2 请求已结束')
    for (const texture of [...this._owned]) texture.dispose()
    this._owned.clear()
  }
}
